import React, { useState, useEffect } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Typography,
  AppBar, Toolbar, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  Select, MenuItem, FormControl,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RegistrationReport from './RegistrationReport';
import AdmReport from './AdmReport';
import EnquiryReport from './EnquiryReport';

// ── User Guide content for the Admission Report page ──────────────────────
const admissionUserGuideSteps = [
    {
    section: "🏢 Branch",
    steps: [
      { label: "Branch Dropdown", desc: "Visible only for the Master branch, shown at the top right next to the info button. Select the branch whose data you want in the Registration, Admission and Enquiry reports. Other branches see their own data automatically." },
    ],
  },
  {
    section: "🎓 Student Selection",
    steps: [
      { label: "All Students", desc: "Select this option to generate the admission report for every student across all classes." },
      { label: "Class-wise Students", desc: "Select this option to filter the report by a specific class. A class dropdown will appear for you to choose from." },
    ],
  },
  {
    section: "📋 Filters",
    steps: [
      { label: "EWS", desc: "Filter students by Economically Weaker Section status. Choose 'EWS: Yes' to include only EWS students, or 'EWS: No' for non-EWS students." },
      { label: "Filter by Caste", desc: "Toggle this option to enable caste-based filtering. A caste dropdown will appear. The report will be disabled until a caste is selected." },
      { label: "Filter by Gender", desc: "Toggle this option to enable gender-based filtering. A gender dropdown will appear. The report will be disabled until a gender is selected." },
      { label: "Filter by Stream", desc: "Toggle this option to enable stream-based filtering. A stream dropdown will appear. The report will be disabled until a stream is selected." },
      { label: "Filter by Session", desc: "Toggle this to lock the date range to the full selected academic session (April 1 to March 31). When active, From/To date fields are disabled." },
      { label: "Session Dropdown", desc: "Select the academic/financial year session. The current session is auto-selected by default. Always visible regardless of the Session filter toggle." },
    ],
  },
  {
    section: "📅 Date Range",
    steps: [
      { label: "From Date", desc: "Set the start date for the admission report. Defaults to one month before today within the selected financial year range. Disabled when 'Filter by Session' is active." },
      { label: "To Date", desc: "Set the end date for the admission report. Defaults to today within the selected financial year range. Disabled when 'Filter by Session' is active." },
    ],
  },
  {
    section: "📄 Report Columns",
    steps: [
      { label: "Admission No", desc: "Unique admission number assigned to each student." },
      { label: "Student Name", desc: "Full name of the admitted student." },
      { label: "Mobile No", desc: "Contact number of the student or guardian." },
      { label: "Roll No", desc: "Roll number assigned to the student in their class." },
      { label: "Class & Section", desc: "The class and section in which the student is enrolled." },
      { label: "Father / Mother Name", desc: "Names of the student's parents as recorded during admission." },
      { label: "Transport", desc: "Indicates whether the student is availing school transport (Yes/No)." },
      { label: "Transport Details", desc: "Shows the route name, month range, and fee amount for transport. Displays '-' if no transport is availed." },
      { label: "Caste", desc: "The caste category of the student as recorded during admission." },
      { label: "Stream", desc: "The academic stream chosen by the student (e.g. Science, Commerce, Arts)." },
      { label: "Language", desc: "The language type opted by the student." },
      { label: "Gender", desc: "Gender of the student — M for Male, F for Female." },
      { label: "EWS", desc: "Whether the student was admitted under the EWS (Economically Weaker Section) category." },
      { label: "TC", desc: "Indicates whether a Transfer Certificate has been issued to the student (Yes/No)." },
      { label: "Ledger No.", desc: "The student's fee ledger number for accounting reference." },
    ],
  },
  {
    section: "⬇️ Actions",
    steps: [
      { label: "Generate Report", desc: "Click to fetch admission data based on all selected filters and open a formatted PDF report in a new browser tab. Ensure pop-ups are allowed." },
      { label: "Download CSV", desc: "Click to download the last generated report data as a CSV file. Make sure to generate the report first before downloading." },
    ],
  },
];

// ── Admission User Guide Modal ─────────────────────────────────────────────
const AdmissionUserGuide = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      },
    }}
  >
    {/* ── Gradient header — matches Payment guide style ── */}
    <DialogTitle
      sx={{
        background: "linear-gradient(135deg, #1a237e 0%, #1976d2 50%, #26c6da 100%)",
        color: "#fff",
        fontWeight: 700,
        fontSize: "1.15rem",
        textAlign: "center",
        py: 2.5,
        letterSpacing: "0.04em",
      }}
    >
      📘 Admission Report — User Guide
    </DialogTitle>

    {/* ── Scrollable content ── */}
    <DialogContent
      dividers
      sx={{
        px: 3,
        py: 2.5,
        maxHeight: "62vh",
        overflowY: "auto",
        backgroundColor: "#fafafa",
      }}
    >
      {admissionUserGuideSteps.map((group, gi) => (
        <Box key={gi} sx={{ mb: gi < admissionUserGuideSteps.length - 1 ? 3 : 0 }}>

          {/* Section heading */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.97rem",
              color: "#1565c0",
              mb: 1.5,
              pb: 0.5,
              borderBottom: "2px solid #bbdefb",
            }}
          >
            {group.section}
          </Typography>

          {/* Step list */}
          {group.steps.map((step, si) => (
            <Box
              key={si}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                mb: 1.2,
              }}
            >
              {/* Bullet dot */}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#1976d2",
                  mt: "5px",
                  flexShrink: 0,
                }}
              />

              {/* Step text */}
              <Typography sx={{ fontSize: "0.875rem", color: "#333", lineHeight: 1.6 }}>
                {/* Bold label */}
                <Box component="span" sx={{ fontWeight: 700, color: "#111" }}>
                  {step.label}
                </Box>
                {" — "}
                {step.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}

      {/* Bottom tip */}
      <Box
        sx={{
          mt: 2.5,
          p: 1.5,
          borderRadius: "8px",
          backgroundColor: "#e3f2fd",
          border: "1px solid #90caf9",
        }}
      >
        <Typography sx={{ fontSize: "0.8rem", color: "#1565c0", fontStyle: "italic" }}>
          💡 <strong>Tip:</strong> Always select the <strong>Session</strong> and set your <strong>Date Range</strong> before generating the report for accurate results. Allow pop-ups in your browser so the PDF opens in a new tab.
        </Typography>
      </Box>
    </DialogContent>

    {/* ── Close button ── */}
    <DialogActions sx={{ justifyContent: "center", py: 2, backgroundColor: "#fafafa" }}>
      <Button
        onClick={onClose}
        variant="contained"
        sx={{
          background: "linear-gradient(45deg, #1976d2 30%, #26c6da 90%)",
          borderRadius: "25px",
          px: 5,
          py: 1,
          fontWeight: 600,
          textTransform: "none",
          fontSize: "0.9rem",
          boxShadow: "0 3px 8px rgba(25,118,210,0.35)",
          "&:hover": {
            background: "linear-gradient(45deg, #1565c0 30%, #00acc1 90%)",
          },
        }}
      >
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

// ── Main AdmissionReport Component ────────────────────────────────────────
function AdmissionReport() {
  // Controls the User Guide modal open/close
  const [guideOpen, setGuideOpen] = useState(false);

    const apiUrl = process.env.REACT_APP_BASE_URL;
  const token = sessionStorage.getItem("token");

  // ⚠️ COPY FROM EMPLOYEE PAGE: how it detects master branch + gets the user's own branch id
  const loggedInBranchId = Number(sessionStorage.getItem("branchId")) || 0;
  const isMasterBranch = sessionStorage.getItem("is_Master") === "true"; // adjust to match employee page

 const [branchList, setBranchList] = useState([]);
const [branchError, setBranchError] = useState("");
const [selectedBranchId, setSelectedBranchId] = useState(isMasterBranch ? "" : loggedInBranchId);

useEffect(() => {
  if (!isMasterBranch) return;

  const fetchBranches = async () => {
    try {
      // ⚠️ Replace URL/method/body with what the employee page uses (see step 3)
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });

      console.log("BRANCH API STATUS:", response.status);
      if (!response.ok) throw new Error(`Status ${response.status}`);

      const data = await response.json();
      console.log("BRANCH API RESPONSE:", data);

      // Accept [..] or { data: [..] } or { result: [..] }
      const rawList = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.result)
        ? data.result
        : [];

      // Tolerate different field-name casings
      const list = rawList
        .map((b) => ({
          id: b.branchId ?? b.branchID ?? b.BranchId ?? b.BranchID ?? b.id,
          name: b.branchName ?? b.BranchName ?? b.name ?? b.branch ?? `Branch ${b.branchId ?? b.id}`,
        }))
        .filter((b) => b.id !== undefined && b.id !== null);

      setBranchList(list);
      if (list.length === 0) setBranchError("No branches found");

      // ✅ AUTO-SELECT: logged-in branch if it's in the list, else the first branch
      setSelectedBranchId((prev) => {
        if (prev) return prev;
        const own = list.find((b) => Number(b.id) === loggedInBranchId);
        return own ? own.id : list[0]?.id ?? "";
      });
    } catch (error) {
      console.error("Branch fetch failed:", error);
      setBranchError("Could not load branches");
    }
  };

  fetchBranches();
}, []); // eslint-disable-line react-hooks/exhaustive-deps 

  return (
    <div className='container'>

      {/* ── AppBar with ℹ button pinned to the right ── */}
      <AppBar position="static" style={{ backgroundColor: "#0B1F40" }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>

          {/* Page title */}
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Admission Report
          </Typography>

                   <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>

            {/* Branch dropdown — Master branch only */}
           {isMasterBranch && (
  <FormControl size="small" sx={{ minWidth: 200 }}>
    <Select
      value={branchList.some((b) => b.id === selectedBranchId) ? selectedBranchId : ""}
      onChange={(e) => setSelectedBranchId(e.target.value)}
      displayEmpty
      sx={{
        color: "#fff",
        backgroundColor: "rgba(255,255,255,0.12)",
        borderRadius: "8px",
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.6)" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
        "& .MuiSvgIcon-root": { color: "#fff" },
      }}
    >
      <MenuItem value="" disabled>
        {branchError || "Select Branch"}
      </MenuItem>
      {branchList.map((b) => (
        <MenuItem key={b.id} value={b.id}>
          {b.name}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)}

            {/* ℹ Info button (existing code, unchanged) */}
          <IconButton
  onClick={() => setGuideOpen(true)}
  size="small"
  sx={{
    width: 34,
    height: 34,
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.15)",
    border: "2px solid rgba(255,255,255,0.7)",
    color: "#fff",
    fontWeight: 900,
    fontSize: "1rem",
    fontFamily: "serif",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.3)",
      border: "2px solid #fff",
      transform: "scale(1.12)",
      boxShadow: "0 0 12px rgba(255,255,255,0.4)",
    },
  }}
>
  i
</IconButton>
          </Box>

        </Toolbar>
      </AppBar>

      {/* ── User Guide Modal ── */}
      <AdmissionUserGuide open={guideOpen} onClose={() => setGuideOpen(false)} />

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
          <Typography variant="h6">Registration Report</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <RegistrationReport branchId={selectedBranchId} />
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header">
          <Typography variant="h6">Admission Report</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <AdmReport branchId={selectedBranchId} />
          </Typography>
        </AccordionDetails>
      </Accordion>

       <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel3a-content" id="panel3a-header">
          <Typography variant="h6">Enquiry Report</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <EnquiryReport branchId={selectedBranchId} />
          </Typography>
        </AccordionDetails>
      </Accordion>

    </div>
  );
}

export default AdmissionReport;